import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Img from '../Img';
import SimpleCheckBox from '../Form/SimpleCheckBox/SimpleCheckBox';
import links from '../../../config/links';

import styles from './popupV3.scss';

export const PopupTypes = {
    INFO: 'info',
    SUCCESS: 'success',
    ERROR: 'error',
};

const icons = {
    info: '/img/v4/circle-info-black.svg',
    success: '/img/v4/circle-check-green.svg',
    error: '/img/v4/triangle-exclamation-red.svg',
};

function PopupV3(props) {
    const {
        title,
        message,
        type,
        isOpen,
        confirmButtonLabel,
        onConfirmClick,
        showCancelButton,
        showConfirmButton,
        cancelButtonLabel,
        onCancelClick,
        onCloseButtonClick,
        noOverlay,
        showCloseButton = false,
        customContent = false,
        noIcon = false,
        enableDoNotShowAgain = false,
        reverseButtonOrder = false,
        className,
        isScrollable,
        'data-testid': dataTestId,
    } = props;

    const [doNotShowChecked, setDoNotShowChecked] = useState(false);

    if (!isOpen) {
        return null;
    }

    let { showSupportLink } = props;
    let errorMessage = '';

    const isErrorPopup = type === PopupTypes.ERROR;

    if (isErrorPopup && !customContent) {
        errorMessage = message?.response?.data?.DisplayMessage || message;
        const useDefaultMessage = !errorMessage || typeof errorMessage !== 'string';
        if (useDefaultMessage) {
            errorMessage = 'There was an issue processing your request.';
            showSupportLink = true;
        }
    } else {
        errorMessage = message;
        showSupportLink = false;
    }

    const checkAndStopClickPropagation = (e) => {
        const hasRouteClick = e?.target?.attributes?.href;
        e.stopPropagation();
        if (!hasRouteClick) {
            e.preventDefault();
        }
    };

    const handleCancelIconClick = (e) => {
        checkAndStopClickPropagation(e);
        // the checkbox value should only propagate when action buttons are clicked
        if (onCloseButtonClick) {
            onCloseButtonClick({ doNotShowAgain: false });
        } else {
            onCancelClick({ doNotShowAgain: false });
        }
    };

    const messageToDisplay = isErrorPopup ? errorMessage : message;
    const isMessageTypeString = typeof messageToDisplay === 'string';

    if (noOverlay) {
        return (
            <div
                className={`${styles.popup} ${isScrollable ? styles.scrollable : ''} ${styles[type]} ${className}`}
                onClick={checkAndStopClickPropagation}
            >
                {showCloseButton && (
                    <div className={styles.closeButton} onClick={handleCancelIconClick} data-testid={dataTestId && `${dataTestId}.close-btn`}>
                        <Img src="/img/v4/close-popup-circle.svg" />
                    </div>
                )}
                <div className={styles.popupContent}>
                    {!noIcon && (
                        <div className={styles.left}>
                            <Img src={icons[type]} />
                        </div>
                    )}
                    <div className={styles.right}>
                        {title && (
                            <div className={styles.title} data-testid={dataTestId && `${dataTestId}.title`}>{title}</div>
                        )}
                        {isMessageTypeString ? (
                            <div
                                onClick={checkAndStopClickPropagation}
                                dangerouslySetInnerHTML={{ __html: messageToDisplay }}
                                data-testid={dataTestId && `${dataTestId}.message`}
                            />
                        ) : (
                            <div onClick={checkAndStopClickPropagation} data-testid={dataTestId && `${dataTestId}.message`}>
                                {messageToDisplay}
                            </div>
                        )}
                        {showSupportLink && (
                            <p>
                                Please try again or contact our <a target="_blank" rel="noopener noreferrer" href={links.support} data-testid={dataTestId && `${dataTestId}.support-link`}>Support Team</a> for assistance.
                            </p>
                        )}
                    </div>
                </div>
                {(showConfirmButton || showCancelButton) && (
                    <div className={styles.buttonsContainer}>
                        {showConfirmButton && (
                            <button type="submit" className={`btn btn-primary btn-small text-uppercase`} onClick={onConfirmClick} data-testid={dataTestId && `${dataTestId}.confirm-btn`}>
                                {confirmButtonLabel}
                            </button>
                        )}
                        {showCancelButton && (
                            <button type="button" className={`btn btn-default btn-small text-uppercase`} onClick={onCancelClick} data-testid={dataTestId && `${dataTestId}.cancel-btn`}>
                                {cancelButtonLabel}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={styles.overlay} onClick={handleCancelIconClick} data-testid="popup.v3.overlay">
            <div className={`${styles.popup} ${isScrollable ? styles.scrollable : ''} ${styles[type]} ${className}`} onClick={(e) => e.stopPropagation()}>
                {showCloseButton && (
                    <div className={styles.closeButton} onClick={handleCancelIconClick} data-testid={dataTestId && `${dataTestId}.close-btn`}>
                        <Img src="/img/v4/close-popup-circle.svg" />
                    </div>
                )}
                <div className={styles.popupContent}>
                    {!noIcon && (
                        <div className={styles.left}>
                            <Img src={icons[type]} />
                        </div>
                    )}
                    <div className={styles.container}>
                        {title && (
                            <div className={styles.title} data-testid={dataTestId && `${dataTestId}.title`}>{title}</div>
                        )}
                        {isMessageTypeString && (
                            <div
                                className={styles.body}
                                onClick={checkAndStopClickPropagation}
                                dangerouslySetInnerHTML={{ __html: messageToDisplay }}
                                data-testid={dataTestId && `${dataTestId}.message`}
                            />
                        )}
                        {!isMessageTypeString && (
                            <div className={styles.body} data-testid={dataTestId && `${dataTestId}.message`}>
                                {messageToDisplay}
                            </div>
                        )}
                        {showSupportLink && (
                            <p>
                                Please try again or contact our <a target="_blank" rel="noopener noreferrer" href={links.support} data-testid={dataTestId && `${dataTestId}.support-link`}>Support Team</a> for assistance.
                            </p>
                        )}
                    </div>
                </div>
                {(showConfirmButton || showCancelButton) && (
                    <div className={styles.buttonsContainer}>
                        {reverseButtonOrder ? (
                            <>
                                {showCancelButton && (
                                    <button
                                        type="button"
                                        className={`btn btn-default btn-small text-uppercase`}
                                        onClick={() => { onCancelClick({ doNotShowAgain: doNotShowChecked }); }}
                                        data-testid={dataTestId && `${dataTestId}.cancel-btn`}
                                    >
                                        {cancelButtonLabel}
                                    </button>
                                )}
                                {showConfirmButton && (
                                    <button
                                        type="submit"
                                        className={`btn btn-primary btn-small text-uppercase`}
                                        onClick={() => { onConfirmClick({ doNotShowAgain: doNotShowChecked }); }}
                                        data-testid={dataTestId && `${dataTestId}.confirm-btn`}
                                    >
                                        {confirmButtonLabel}
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                {showConfirmButton && (
                                    <button
                                        type="submit"
                                        className={`btn btn-primary btn-small text-uppercase`}
                                        onClick={() => { onConfirmClick({ doNotShowAgain: doNotShowChecked }); }}
                                        data-testid={dataTestId && `${dataTestId}.confirm-btn`}
                                    >
                                        {confirmButtonLabel}
                                    </button>
                                )}
                                {showCancelButton && (
                                    <button
                                        type="button"
                                        className={`btn btn-default btn-small text-uppercase`}
                                        onClick={() => { onCancelClick({ doNotShowAgain: doNotShowChecked }); }}
                                        data-testid={dataTestId && `${dataTestId}.cancel-btn`}
                                    >
                                        {cancelButtonLabel}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}
                {enableDoNotShowAgain && (
                    <div className={styles.doNotShowAgain}>
                        <SimpleCheckBox
                            id="popupV3-do-not-show-again"
                            customLabel="Do not show this message again"
                            checked={doNotShowChecked}
                            onChange={setDoNotShowChecked}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

PopupV3.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    confirmButtonLabel: PropTypes.string,
    cancelButtonLabel: PropTypes.string,
    onCancelClick: PropTypes.func,
    onConfirmClick: PropTypes.func,
    onCloseButtonClick: PropTypes.func,
    title: PropTypes.string,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.element, PropTypes.node]),
    type: PropTypes.string,
    showCancelButton: PropTypes.bool,
    showConfirmButton: PropTypes.bool,
    showSupportLink: PropTypes.bool,
    noOverlay: PropTypes.bool,
    showCloseButton: PropTypes.bool,
    customContent: PropTypes.bool,
    noIcon: PropTypes.bool,
    enableDoNotShowAgain: PropTypes.bool,
    reverseButtonOrder: PropTypes.bool,
    className: PropTypes.string,
    'data-testid': PropTypes.string,
};

PopupV3.defaultProps = {
    confirmButtonLabel: 'Confirm',
    cancelButtonLabel: 'Cancel',
    onCancelClick: () => { },
    onConfirmClick: () => { },
    onCloseButtonClick: null,
    showCancelButton: false,
    showConfirmButton: true,
    showSupportLink: false,
    noOverlay: false,
    showCloseButton: false,
    title: null,
    message: null,
    customContent: false,
    noIcon: false,
    enableDoNotShowAgain: false,
    reverseButtonOrder: false,
    className: '',
    type: PopupTypes.INFO,
    'data-testid': null,
};

export default PopupV3;
